import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity } from "lucide-react";
import { fetchDataHealth } from "@/lib/api";

const rel = (iso) => {
  if (!iso) return "never";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const DOT = { ok: "bg-emerald-500", warn: "bg-amber-500", down: "bg-red-500" };
const TXT = { ok: "text-pos", warn: "text-amber-term", down: "text-neg" };
const LABEL = { ok: "ALL LIVE", warn: "STALE", down: "STALE" };

export default function DataHealthIndicator() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside-click / Escape. A `fixed inset-0` overlay can't be used here
  // because TopBar's header uses backdrop-blur, which makes the header the containing
  // block for fixed children — the overlay would clip to the bar and trap the panel.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const { data } = useQuery({
    queryKey: ["data-health"],
    queryFn: fetchDataHealth,
    refetchInterval: 120000,
    staleTime: 60000,
    refetchOnWindowFocus: true,
  });

  const overall = data?.overall || "ok";
  const counts = data?.counts || { ok: 0, warn: 0, down: 0 };
  const feeds = data?.feeds || [];
  const issues = counts.warn + counts.down;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        data-testid="data-health-indicator"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-zinc-500 hover:text-amber-term transition-colors"
        title="Data feed freshness"
      >
        <span className={`relative inline-flex h-2 w-2 rounded-full ${DOT[overall]}`}>
          {overall !== "ok" && (
            <span className={`absolute inline-flex h-full w-full rounded-full ${DOT[overall]} opacity-60 animate-ping`} />
          )}
        </span>
        <span className={`hidden sm:inline ${TXT[overall]}`} data-testid="data-health-label">
          {LABEL[overall]}
          {issues > 0 ? ` ${issues}` : ""}
        </span>
      </button>

      {open && (
        <div
          data-testid="data-health-panel"
          className="absolute right-0 mt-2 w-80 max-h-[460px] overflow-auto border border-zinc-700 bg-[#09090B] z-40 shadow-2xl"
        >
            <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-[#09090B]">
              <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-400">
                <Activity size={11} strokeWidth={1.5} /> Data Feeds
              </span>
              <span className="flex items-center gap-2 text-[11px] uppercase tracking-widest">
                <span className="text-pos">{counts.ok} live</span>
                {counts.warn > 0 && <span className="text-amber-term">{counts.warn} stale</span>}
                {counts.down > 0 && <span className="text-neg">{counts.down} down</span>}
              </span>
            </div>

            {feeds.length === 0 ? (
              <div className="px-3 py-10 text-center text-[11px] uppercase tracking-widest text-zinc-600">
                Checking feeds…
              </div>
            ) : (
              feeds.map((f) => (
                <div
                  key={f.key}
                  data-testid={`data-health-feed-${f.key}`}
                  className="px-3 py-2 border-b border-zinc-900 flex items-center gap-2"
                >
                  <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${DOT[f.status]}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] text-zinc-100 leading-snug flex items-center gap-1.5">
                      {f.label}
                      <span className="text-zinc-600 text-[10px] uppercase tracking-widest">{f.category}</span>
                    </div>
                    {f.as_of && (
                      <div className="text-[10px] uppercase tracking-widest text-zinc-600 leading-snug">
                        latest · {f.as_of}
                      </div>
                    )}
                  </div>
                  <span className={`text-[11px] uppercase tracking-widest shrink-0 ${TXT[f.status]}`}>
                    {rel(f.updated_at)}
                  </span>
                </div>
              ))
            )}
            <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-600">
              Refreshed from source · reconciled daily
            </div>
        </div>
      )}
    </div>
  );
}
